/**
 * TadilatTalepAdminPage.jsx — Bölüm 25-A
 *
 * Sayfa her zaman AKTİF FUARI gösterir (MainContext.selectedFuarId; fuar değiştirme
 * yeri başlıktaki global seçici). 2026-09-12'ye kadar sayfanın kendi "Fuar Seçin"
 * açılır listesi vardı ve boş açılıyordu — bir fuar seçilmeden hiçbir şey yüklenmiyordu.
 *
 *  - Token kartı: mevcut token görüntüle / oluştur / yenile / kopyala
 *  - Başvuru tablosu: liste, filtre, sayfalama, detay modal, sil modal
 */
import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import {
  Container, Card, CardBody, CardHeader,
  Row, Col, Button, Input, Badge, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Table, InputGroup,
} from "reactstrap";
import {
  Key, RefreshCw, Copy, Check, Eye, Trash2,
  ExternalLink, ChevronLeft, ChevronRight,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import { fuarService } from "../../../services/fuarService";
import {
  generateToken,
  getToken,
  getSubmissions,
  getSubmission,
  deleteSubmission,
} from "../../../services/tadilaTalepService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

// ─── helpers ─────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const FALLBACK_FUAR_ID = 298;

// ─── PublicFormUrl helper ─────────────────────────────────────────────────────
function buildPublicUrl(token) {
  return `${window.location.origin}/tadilat-form?token=${token}`;
}

// ─── Token Kartı ─────────────────────────────────────────────────────────────
function TokenCard({ fuarId, onTokenReady }) {
  const { t: tx } = useTranslation("yonetim");
  const [tokenData, setTokenData]       = useState(null);
  const [loading, setLoading]           = useState(false);
  const [generating, setGenerating]     = useState(false);
  const [error, setError]               = useState(null);
  const [copied, setCopied]             = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  // triggerLoad: fuar değişikliği veya generate sonrası reload için sayaç
  const [loadTrigger, setLoadTrigger]   = useState(0);

  // ─ loadToken → useEffect içinde inner async fn, doğrudan setState yok ─
  useEffect(() => {
    if (!fuarId) return;
    let cancelled = false;

    async function loadToken() {
      setLoading(true);
      setError(null);
      try {
        const res = await getToken(fuarId);
        if (cancelled) return;
        // Olası response formatları:
        // 1) { result, data: { token: "..." }, message }  → res?.data?.token
        // 2) { token: "..." }                              → res?.token
        // 3) "abc123" (raw string)                        → res
        const tok = res?.data?.token
          ?? res?.token
          ?? (typeof res === 'string' ? res : null);
        setTokenData(tok);
        if (tok) onTokenReady?.(tok);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setTokenData(null);
        } else {
          setError(tx("tadilatTalepAdmin.tokenAlinirkenHataOlustu"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadToken();
    return () => { cancelled = true; };
    // onTokenReady kasıtlı olarak dep dışı — parent inline fn ise döngü olur;
    // ref ile takip ediyoruz
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuarId, loadTrigger]);

  const onTokenReadyRef = useRef(onTokenReady);
  useEffect(() => { onTokenReadyRef.current = onTokenReady; }, [onTokenReady]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setConfirmRegen(false);
    try {
      const res = await generateToken(fuarId);
      // Olası response formatları:
      // 1) { result, data: { token: "..." }, message }  → res?.data?.token
      // 2) { token: "..." }                              → res?.token
      // 3) "abc123" (raw string)                        → res
      const tok = res?.data?.token
        ?? res?.token
        ?? (typeof res === 'string' ? res : null);
      setTokenData(tok);
      if (tok) onTokenReadyRef.current?.(tok);
    } catch {
      setError(tx("tadilatTalepAdmin.tokenOlusturulurkenHataOlustu"));
    } finally {
      setGenerating(false);
      setLoadTrigger((t) => t + 1);
    }
  };

  const handleCopy = () => {
    if (!tokenData) return;
    navigator.clipboard.writeText(buildPublicUrl(tokenData)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Card className="border-0 shadow-sm h-100">
      <CardHeader className="bg-primary text-white d-flex align-items-center gap-2">
        <Key size={16} />
        <span className="fw-semibold">{tx("tadilatTalepAdmin.publicFormToken")}</span>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" />{" "}{tx("tadilatTalepAdmin.yukleniyor")}</div>
        ) : error ? (
          <Alert color="danger" className="mb-0">{error}</Alert>
        ) : tokenData ? (
          <>
            <p className="text-muted small mb-1">{tx("tadilatTalepAdmin.mevcutToken")}</p>
            <div className="bg-light rounded p-2 font-monospace small text-break mb-3">
              {tokenData}
            </div>
            <p className="text-muted small mb-1">{tx("tadilatTalepAdmin.publicFormUrl")}</p>
            <div className="bg-light rounded p-2 small text-break mb-3">
              <a href={buildPublicUrl(tokenData)} target="_blank" rel="noopener noreferrer" className="text-primary">
                {buildPublicUrl(tokenData)}
              </a>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button color="success" size="sm" onClick={handleCopy} className="d-flex align-items-center gap-1">
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? tx("tadilatTalepAdmin.kopyalandi") : tx("tadilatTalepAdmin.urlKopyala")}
              </Button>
              <Button color="warning" size="sm" disabled={generating} onClick={() => setConfirmRegen(true)} className="d-flex align-items-center gap-1">
                <RefreshCw size={14} className={generating ? "spin" : ""} />
                {tx("common:yenile")}
              </Button>
              <Button color="info" size="sm" tag="a" href={buildPublicUrl(tokenData)} target="_blank" className="d-flex align-items-center gap-1 text-white">
                <ExternalLink size={14} />{" "}{tx("tadilatTalepAdmin.formuAc")}
              </Button>
            </div>
            {/* Yenile onay modal */}
            <Modal isOpen={confirmRegen} toggle={() => setConfirmRegen(false)} centered size="sm">
              <ModalHeader toggle={() => setConfirmRegen(false)}>{tx("tadilatTalepAdmin.tokenYenile")}</ModalHeader>
              <ModalBody>
                <Alert color="warning" className="mb-0">
                  {tx("tadilatTalepAdmin.tokenYenilenirseMevcutTumPublic")}{" "}<strong>{tx("tadilatTalepAdmin.gecersiz")}</strong>{" "}{tx("tadilatTalepAdmin.olacaktirEminMisiniz")}
                </Alert>
              </ModalBody>
              <ModalFooter>
                <Button color="warning" disabled={generating} onClick={handleGenerate}>
                  {generating ? <Spinner size="sm" /> : tx("tadilatTalepAdmin.evetYenile")}
                </Button>
                <Button color="secondary" onClick={() => setConfirmRegen(false)}>{tx("common:iptal")}</Button>
              </ModalFooter>
            </Modal>
          </>
        ) : (
          <>
            <p className="text-muted mb-3">{tx("tadilatTalepAdmin.buFuarIcinHenuzToken")}</p>
            <Button color="primary" disabled={generating} onClick={handleGenerate} className="d-flex align-items-center gap-2">
              {generating ? <Spinner size="sm" /> : <Key size={14} />}
              {tx("tadilatTalepAdmin.tokenOlustur")}
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ─── SubmissionDetail alt bileşenleri — dosya seviyesinde, render içinde DEĞİL ─
// (ESLint: react-hooks/static-components — "Cannot create components during render")
function Section({ title, children }) {
  return (
    <div className="mb-4">
      <h6 className="fw-bold text-primary border-bottom pb-1 mb-3">{title}</h6>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <Row className="mb-2">
      <Col xs={5} className="text-muted small fw-semibold">{label}</Col>
      <Col xs={7} className="small">{value || "—"}</Col>
    </Row>
  );
}

// ─── Başvuru Detay Modalı ─────────────────────────────────────────────────────
function DetailModal({ id, isOpen, onClose }) {
  const { t } = useTranslation("yonetim");
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // inner async fn — effect body'de doğrudan setState yok
  useEffect(() => {
    if (!isOpen || !id) return;
    let cancelled = false;

    async function fetchDetail() {
      setLoading(true);
      setError(null);
      try {
        const res = await getSubmission(id);
        if (!cancelled) setData(res?.data ?? null);
      } catch {
        if (!cancelled) setError(i18n.t("yonetim:tadilatTalepAdmin.detayYuklenirkenHataOlustu"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDetail();
    return () => { cancelled = true; };
  }, [isOpen, id]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" scrollable centered>
      <ModalHeader toggle={onClose}>{t("tadilatTalepAdmin.basvuruDetayi")}</ModalHeader>
      <ModalBody>
        {loading && <div className="text-center py-4"><Spinner /></div>}
        {error   && <Alert color="danger">{error}</Alert>}
        {!loading && !error && data && <SubmissionDetail data={data} />}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>{t("common:kapat")}</Button>
      </ModalFooter>
    </Modal>
  );
}

function SubmissionDetail({ data }) {
  const { t } = useTranslation("yonetim");
  const d = data;
  // Section ve Field bileşenleri dosya seviyesinde tanımlı (yukarıda)
  return (
    <div>
      <Section title={t("tadilatTalepAdmin.fuarStandBilgileri")}>
        <Field label={t("tadilatTalepAdmin.fuarId")}  value={d.fuarId} />
        <Field label={t("tadilatTalepAdmin.salon")}    value={d.salonAdi} />
        <Field label={t("tadilatTalepAdmin.standNo")} value={d.standNo} />
        <Field label={t("tadilatTalepAdmin.standId")} value={d.standId} />
        <Field label={t("tadilatTalepAdmin.tarih")}    value={fmtDate(d.createdAt)} />
      </Section>
      <Section title={t("tadilatTalepAdmin.talepEdenKisi")}>
        <Field label={t("tadilatTalepAdmin.firma")}    value={d.katilimciFirmaAdi} />
        <Field label={t("tadilatTalepAdmin.adSoyad")} value={d.talepKisi?.adSoyad} />
        <Field label={t("tadilatTalepAdmin.telefon")}  value={d.talepKisi?.telefon} />
      </Section>
      <Section title={t("tadilatTalepAdmin.dekoratorBilgileri")}>
        <Field label={t("tadilatTalepAdmin.firmaAdi")}        value={d.dekorator?.firmaAdi} />
        <Field label={t("tadilatTalepAdmin.yetkiliAdSoyad")} value={d.dekorator?.yetkiliAdSoyad} />
        <Field label={t("tadilatTalepAdmin.yetkiliTelefon")}  value={d.dekorator?.yetkiliTelefon} />
      </Section>
      <Section title={t("tadilatTalepAdmin.tadilatBilgileri")}>
        <Field label={t("tadilatTalepAdmin.yapilacakIs")}  value={d.tadilat?.yapilacakIs} />
        <Field label={t("tadilatTalepAdmin.tahminiSure")}  value={d.tadilat?.tahminiSure} />
      </Section>
      {d.personeller?.length > 0 && (
        <Section title={t("tadilatTalepAdmin.personelListesi")}>
          <Table size="sm" bordered responsive className="small">
            <thead className="table-light">
              <tr><th>#</th><th>{t("tadilatTalepAdmin.ad")}</th><th>{t("tadilatTalepAdmin.soyad")}</th><th>{t("tadilatTalepAdmin.tckn")}</th></tr>
            </thead>
            <tbody>
              {d.personeller.map((p, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{p.ad}</td>
                  <td>{p.soyad}</td>
                  <td className="font-monospace">{p.tckn}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      )}
    </div>
  );
}

// ─── Silme Onay Modalı ────────────────────────────────────────────────────────
function DeleteModal({ id, standNo, isOpen, onClose, onDeleted }) {
  const { t } = useTranslation("yonetim");
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteSubmission(id);
      onDeleted();
      onClose();
    } catch {
      setError(t("tadilatTalepAdmin.silmeIslemiBasarisiz"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="sm">
      <ModalHeader toggle={onClose}>{t("tadilatTalepAdmin.basvuruyuSil")}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        <p>
          <strong>{standNo || id}</strong>{" "}{t("tadilatTalepAdmin.noLuStandBasvurusuKalici")}
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="danger" disabled={deleting} onClick={handleDelete}>
          {deleting ? <Spinner size="sm" /> : <><Trash2 size={14} className="me-1" />{t("common:sil")}</>}
        </Button>
        <Button color="secondary" onClick={onClose}>{t("common:iptal")}</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
const TadilatTalepAdminPage = () => {
  const { t } = useTranslation("yonetim");
  const { selectedFuarId: ctxFuarId } = useContext(MainContext);
  const fuarId = Number(ctxFuarId)
    || Number(localStorage.getItem("activeFairId"))
    || FALLBACK_FUAR_ID;
  const [fuarName, setFuarName] = useState("");

  // submissions state
  const [submissions, setSubmissions]     = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [totalPages, setTotalPages]       = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage]                   = useState(0);
  const [pageSize, setPageSize]           = useState(25);
  const [standFilter, setStandFilter]     = useState("");
  const [standFilterInput, setStandFilterInput] = useState("");

  // refreshKey: Yenile butonu / silme sonrası listeyi tetiklemek için sayaç
  // useCallback+useEffect([fn]) zincirini kaldırarak ESLint uyarısını önler
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // modals
  const [detailId, setDetailId]         = useState(null);
  const [showDetail, setShowDetail]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDelete, setShowDelete]     = useState(false);

  // ─ Aktif fuarın adı — yalnızca başlık etiketi için ─
  useEffect(() => {
    let cancelled = false;

    async function fetchFuarName() {
      try {
        const d = await fuarService.getFuarDetail(fuarId);
        if (cancelled) return;
        setFuarName(d?.fuarTexts?.[0]?.fuarName || d?.fuar?.fuarName || d?.fuarName || "");
      } catch {
        if (!cancelled) setFuarName("");
      }
    }

    fetchFuarName();
    return () => { cancelled = true; };
  }, [fuarId]);

  // ─ Başvuru listesi — inner async fn + refreshKey trigger ─
  useEffect(() => {
    let cancelled = false;

    async function fetchSubmissions() {
      setLoading(true);
      setError(null);
      try {
        const res = await getSubmissions({
          fuarId,
          standId: standFilter || undefined,
          page,
          limit: pageSize,
        });
        if (cancelled) return;
        // API response: { total, page, limit, records: [...] }
        const content = res?.records ?? [];
        setSubmissions(Array.isArray(content) ? content : []);
        const total  = res?.total ?? 0;
        const limit  = res?.limit ?? pageSize;
        setTotalPages(Math.ceil(total / limit));
        setTotalElements(total);
      } catch {
        if (!cancelled) setError(i18n.t("yonetim:tadilatTalepAdmin.basvurularYuklenirkenHataOlustu"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchSubmissions();
    return () => { cancelled = true; };
  }, [fuarId, page, pageSize, standFilter, refreshKey]);

  const handleStandFilterSearch = () => {
    setStandFilter(standFilterInput.trim());
    setPage(0);
  };

  const openDetail = (id) => { setDetailId(id); setShowDetail(true); };
  const openDelete = (id, standNo) => { setDeleteTarget({ id, standNo }); setShowDelete(true); };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("tadilatTalepAdmin.tadilatTalepYonetimi")} pageTitle={t("tadilatTalepAdmin.yonetim")} />

        {/* Aktif fuar — seçici yok, başlıktaki global fuar seçimi geçerli */}
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <small className="text-muted">
            {t("tadilatTalepAdmin.aktifFuar")}{" "}<strong>{fuarName || `#${fuarId}`}</strong>
          </small>
          <Badge color="primary" className="px-3 py-1 rounded-pill">
            {t("tadilatTalepAdmin.basvuru", { totalElements })}
          </Badge>
        </div>

        <Row className="g-4 mb-4">
          {/* Token Kartı */}
          <Col lg={4}>
            <TokenCard fuarId={fuarId} />
          </Col>

          {/* Filtre + Tablo */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm h-100">
              <CardHeader className="bg-light d-flex justify-content-between align-items-center">
                <span className="fw-semibold">{t("tadilatTalepAdmin.basvurular")}</span>
                <Button color="light" size="sm" onClick={triggerRefresh} className="d-flex align-items-center gap-1">
                  <RefreshCw size={13} />{" "}{t("common:yenile")}
                </Button>
              </CardHeader>
              <CardBody>
                {/* Filtre */}
                <Row className="mb-3 g-2 align-items-end">
                  <Col xs={8} md={6}>
                    <label className="form-label small mb-1">{t("tadilatTalepAdmin.standIdFiltrele")}</label>
                    <InputGroup>
                      <Input
                        placeholder={t("tadilatTalepAdmin.standIdSayisal")}
                        value={standFilterInput}
                        onChange={(e) => setStandFilterInput(e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => e.key === "Enter" && handleStandFilterSearch()}
                      />
                      <Button color="secondary" onClick={handleStandFilterSearch}>{t("common:ara")}</Button>
                    </InputGroup>
                  </Col>
                  <Col xs={4} md={3}>
                    <label className="form-label small mb-1">{t("tadilatTalepAdmin.sayfaBoyutu")}</label>
                    <Input
                      type="select"
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                    >
                      {PAGE_SIZE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                {/* Hata */}
                {error && <Alert color="danger">{error}</Alert>}

                {/* Tablo */}
                {loading ? (
                  <div className="text-center py-5"><Spinner /></div>
                ) : submissions.length === 0 ? (
                  <div className="text-center text-muted py-5">{t("tadilatTalepAdmin.basvuruBulunamadi")}</div>
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm" className="align-middle small mb-2">
                      <thead className="table-light">
                        <tr>
                          <th>{t("tadilatTalepAdmin.tarih")}</th>
                          <th>{t("tadilatTalepAdmin.standNo")}</th>
                          <th>{t("tadilatTalepAdmin.salon")}</th>
                          <th>{t("tadilatTalepAdmin.firma")}</th>
                          <th>{t("tadilatTalepAdmin.talepEden")}</th>
                          <th className="text-center">{t("tadilatTalepAdmin.islem")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.map((s) => (
                          <tr key={s.id}>
                            <td className="text-nowrap">{fmtDate(s.createdAt)}</td>
                            <td><Badge color="secondary" className="font-monospace">{s.standNo || "—"}</Badge></td>
                            <td>{s.salonAdi || "—"}</td>
                            <td>{s.katilimciFirmaAdi || "—"}</td>
                            <td>{s.talepKisi?.adSoyad || "—"}</td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button
                                  color="primary" size="sm" outline
                                  className="p-1 lh-1"
                                  title={t("common:detay")}
                                  onClick={() => openDetail(s.id)}
                                >
                                  <Eye size={13} />
                                </Button>
                                <Button
                                  color="danger" size="sm" outline
                                  className="p-1 lh-1"
                                  title={t("common:sil")}
                                  onClick={() => openDelete(s.id, s.standNo)}
                                >
                                  <Trash2 size={13} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
                    <Button
                      color="light" size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft size={14} />
                    </Button>
                    <span className="small text-muted">
                      {page + 1} / {totalPages}
                    </span>
                    <Button
                      color="light" size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Detail Modal — key={detailId} → değişince unmount/mount, state sıfırlanır */}
      <DetailModal
        key={detailId}
        id={detailId}
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
      />

      {/* Delete Modal — key={deleteTarget?.id} → değişince unmount/mount */}
      {deleteTarget && (
        <DeleteModal
          key={deleteTarget.id}
          id={deleteTarget.id}
          standNo={deleteTarget.standNo}
          isOpen={showDelete}
          onClose={() => setShowDelete(false)}
          onDeleted={triggerRefresh}
        />
      )}
    </div>
  );
};

export default TadilatTalepAdminPage;





